import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { Button, Flex, Label, Text, Grid, TextArea, Icon, Select, Option, Input } from '@pega/cosmos-react-core';
import useLocalTranslation from '../../../../hooks/useLocalTranslation';
import { getAzureModels, updateCustomSimulator } from '../AISimulatorApis';
import { useDispatch } from 'react-redux';
import { updateBannerInfo, updateLoaderDetails } from '../../../../redux/actions';
import { useNavigate } from 'react-router-dom';
import { tokenMaskerStyle } from '../../../Utilities/custom.styled';
import { useForm } from 'react-hook-form';
import * as deepDiff from 'deep-diff';
import { AzureModels, OpenAIHost, OpenAIModels } from '../../../../constants/common';
import { getMaskedKey } from '../../../../utils/helpers';
import CustomLabel from '../../../Utilities/CustomLabel';

interface SimulatorConnectionProps {
  connectionId: string;
  accountDetails: any;
  setAccountDetails: Dispatch<SetStateAction<any>>;
  sessionId: string;
  deleteConfirmation: () => void;
  genAiKeyOption?: string;
}

type ConnectionDetailsFormData = {
  apiKey?: string;
  host?: string;
  clientId?: string;
  azureResource?: string;
  oauthEndpoint?: string;
  selectedModel?: string;
  apiVersion?: string;
  maximumMessages: string;
};

const CustomerAISimulatorConnection: React.FC<SimulatorConnectionProps> = ({
  connectionId,
  accountDetails,
  setAccountDetails,
  sessionId,
  deleteConfirmation,
  genAiKeyOption,
}) => {
  const translate = useLocalTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const defaultMaskValues = { maskApiKey: true, maskClientId: true };
  const {
    host,
    apiKey,
    oauthEndpoint,
    clientId,
    azureResource,
    jwtToken,
    simulationStatus,
    selectedModel,
    apiVersion,
    maximumMessages,
    azureModels,
  } = accountDetails;
  const { register, handleSubmit, errors, formState, reset } = useForm({
    mode: 'all',
  });

  const [maskData, setMaskData] = useState(defaultMaskValues);
  const [isMaskEnabled, setIsMaskEnabled] = useState(true);
  const [editFields, setEditFields] = useState(false);
  const [selectedHost, setSelectedHost] = useState(OpenAIHost.OpenAI);
  const [updatedAzureModels, setAzureModels] = useState(azureModels || AzureModels);

  useEffect(() => {
    setSelectedHost(host || OpenAIHost.OpenAI);
  }, [host]);

  useEffect(() => {
    const updatedData = {
      apiKey: selectedHost !== host ? '' : apiKey,
      clientId: clientId || '',
      oauthEndpoint: oauthEndpoint || '',
      azureResource: azureResource || '',
      selectedModel: selectedModel || (selectedHost === OpenAIHost.Azure ? AzureModels[0].id : OpenAIModels[0].id),
      apiVersion: apiVersion || '',
      maximumMessages: maximumMessages || 75,
    };
    reset(updatedData);
  }, [
    apiKey,
    clientId,
    azureResource,
    selectedModel,
    host,
    selectedHost,
    apiVersion,
    reset,
    oauthEndpoint,
    maximumMessages,
  ]);

  const getMaskedToken = (token: string = '') => {
    return !isMaskEnabled ? token : new Array(token.length).fill('*').join('');
  };

  const editConnectionData = () => {
    setEditFields(true);
  };

  const discardChanges = () => {
    setEditFields(false);
    setSelectedHost(host);
    setMaskData(defaultMaskValues);
    reset();
  };

  const validatePayload = (data: any) => {
    let updatedPayload = { ...data };
    let hasEmptyValue = false;

    Object.keys(data).forEach((key: string) => {
      const replacedVal = typeof data[key] === 'string' ? data[key]?.trim() : data[key];
      if (!replacedVal || replacedVal === '') {
        hasEmptyValue = true;
      }
      updatedPayload[key] = replacedVal;
    });

    if (hasEmptyValue) return !hasEmptyValue;

    const prevValues = {
      host,
      azureResource,
      apiKey,
      clientId,
      oauthEndpoint,
      selectedModel,
      apiVersion,
      maximumMessages,
    };
    const triggerAPI = deepDiff
      .diff(prevValues, updatedPayload)
      ?.filter((item) => (item as any)?.lhs || (item as any)?.rhs).length;
    return triggerAPI;
  };

  const updateConnectionData = async (data: ConnectionDetailsFormData) => {
    const maxMessages = parseInt(data.maximumMessages) || 0;
    const payload = { ...data, host: selectedHost, maximumMessages: maxMessages, azureModels: updatedAzureModels };
    const isValid = validatePayload(payload);
    if (isValid) {
      dispatch(updateLoaderDetails(true));
      const response = await updateCustomSimulator(sessionId, connectionId, payload);
      dispatch(updateLoaderDetails(false));
      if (response?.status === 200) {
        setAccountDetails(response?.data);
        setEditFields(false);
        setMaskData(defaultMaskValues);
        dispatch(updateBannerInfo(translate('updated_connection_configuration'), 'success'));
      } else if (response?.status === 403) {
        navigate('/');
        dispatch(updateBannerInfo(response?.errorMessage ? translate(response?.errorMessage) : '', 'urgent'));
      } else {
        dispatch(updateBannerInfo(response?.errorMessage ? translate(response?.errorMessage) : '', 'urgent'));
      }
    }
  };

  const handleOnSubmit = () => {
    handleSubmit(updateConnectionData)();
  };

  const configValidation = (value: number) => {
    return value < 10 ? translate('maximum_messages_error_1') : true;
  };

  const getModels = async (data: ConnectionDetailsFormData) => {
    const validPayload = data.apiKey && data.apiVersion && data.azureResource && data.clientId && data.oauthEndpoint;
    if (validPayload) {
      dispatch(updateLoaderDetails(true));
      const response = await getAzureModels(sessionId, data);
      dispatch(updateLoaderDetails(false));
      if (response?.status === 200 && response?.data?.length > 0) {
        setAzureModels(response?.data);
      } else {
        dispatch(updateBannerInfo(translate('get_models_error'), 'info'));
      }
    }
  };

  return (
    <>
      <Flex container={{ direction: 'column', itemGap: 2 }} item={{ grow: 1 }}>
        <Text variant="h3">{translate('connection_details')}</Text>

        <Grid
          container={{
            cols: 'repeat(2, 33% 67%)',
            gap: 1,
            alignItems: 'center',
          }}
        >
          <Label>{translate('connection_id')}</Label>
          <Text variant="secondary">{connectionId ? connectionId.replace('custom_', '') : ''}</Text>
        </Grid>
        <Grid
          container={{
            cols: 'repeat(2, 33% 67%)',
            gap: 1,
            alignItems: 'center',
          }}
        >
          <Label>{translate('jwt_secret_label')}</Label>
          <Flex container={{ itemGap: 2, alignItems: 'end' }}>
            <Text variant="secondary">{getMaskedToken(jwtToken)}</Text>
            <Button style={tokenMaskerStyle} onClick={() => setIsMaskEnabled(!isMaskEnabled)}>
              {translate(isMaskEnabled ? 'show_token' : 'hide_token')}
            </Button>
          </Flex>
        </Grid>
        <Text variant="secondary">{translate('jwt_secret_description_3rd_party')}</Text>
        <Text variant="h3" style={{ marginTop: '2rem' }}>
          {translate('gen_ai')}
        </Text>
        <Grid
          container={{
            cols: 'repeat(2, 33% 65%)',
            gap: 1,
            alignItems: 'center',
          }}
        >
          <CustomLabel
            label={translate('maximum_messages')}
            showRequired={editFields ? true : false}
            tooltipInfo={translate('max_messages_tooltip')}
          />
          {editFields ? (
            <Input
              name="maximumMessages"
              label={translate('maximum_messages')}
              autoComplete="off"
              type="number"
              labelHidden={true}
              required={true}
              info={errors.maximumMessages?.message}
              status={errors.maximumMessages?.message ? 'error' : undefined}
              ref={register({
                required: translate('maximum_messages_error'),
                pattern: { value: /^[0-9]*$/, message: translate('maximum_messages_error_2') },
                validate: configValidation,
              })}
            />
          ) : (
            <Text variant="secondary">{maximumMessages ? maximumMessages : 75}</Text>
          )}
        </Grid>
        {genAiKeyOption !== 'pega' && (
          <>
            {selectedHost === OpenAIHost.Azure && (
              <>
                {(azureResource || editFields) && (
                  <Grid
                    container={{
                      cols: 'repeat(2, 33% 65%)',
                      gap: 1,
                      alignItems: 'center',
                    }}
                  >
                    <CustomLabel
                      label={translate('resource')}
                      showRequired={editFields ? true : false}
                      tooltipInfo={translate('resource_tooltip')}
                    />
                    {editFields ? (
                      <Input
                        name="azureResource"
                        label={translate('resource')}
                        autoComplete="off"
                        required={true}
                        labelHidden={true}
                        info={errors.azureResource?.message}
                        status={errors.azureResource?.message ? 'error' : undefined}
                        ref={register({ required: translate('resource_error') })}
                      />
                    ) : (
                      <Text variant="secondary">{azureResource}</Text>
                    )}
                  </Grid>
                )}
                {(oauthEndpoint || editFields) && (
                  <Grid
                    container={{
                      cols: 'repeat(2, 33% 65%)',
                      gap: 1,
                      alignItems: 'center',
                    }}
                  >
                    <Label>
                      {translate('oauth_endpoint')} {editFields && <CustomRequired />}
                    </Label>
                    {editFields ? (
                      <Input
                        name="oauthEndpoint"
                        label={translate('oauth_endpoint')}
                        required={true}
                        labelHidden={true}
                        info={errors.oauthEndpoint?.message}
                        status={errors.oauthEndpoint?.message ? 'error' : undefined}
                        ref={register({ required: translate('oauth_endpoint_error') })}
                      />
                    ) : (
                      <Text variant="secondary">{oauthEndpoint}</Text>
                    )}
                  </Grid>
                )}
              </>
            )}
            {(host || editFields) && (
              <Grid
                container={{
                  cols: 'repeat(2, 33% 65%)',
                  gap: 1,
                  alignItems: 'center',
                }}
              >
                <CustomLabel
                  label={translate('host')}
                  showRequired={editFields ? true : false}
                  tooltipInfo={translate('host_tooltip')}
                />
                {editFields ? (
                  <Select
                    label={translate('host')}
                    required={true}
                    name="host"
                    value={selectedHost}
                    labelHidden={true}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                      setSelectedHost(e.target.value as OpenAIHost);
                    }}
                  >
                    <Option value={OpenAIHost.OpenAI}>{translate('OpenAI')}</Option>
                    <Option value={OpenAIHost.Azure}>{translate('Azure')}</Option>
                    <Option value={OpenAIHost.Pega}>{translate('Pega GenAI')}</Option>
                  </Select>
                ) : (
                  <Text variant="secondary">{translate(host)}</Text>
                )}
              </Grid>
            )}
            {(selectedModel || editFields) && selectedHost === OpenAIHost.OpenAI && (
              <Grid
                container={{
                  cols: 'repeat(2, 33% 65%)',
                  gap: 1,
                  alignItems: 'center',
                }}
              >
                <Label>
                  {translate('selected_model')} {editFields && <CustomRequired />}
                </Label>
                {editFields ? (
                  <Select name="selectedModel" label={translate('selected_model')} labelHidden={true} ref={register()}>
                    {OpenAIModels.map((item) => (
                      <Option value={item.id} key={item.id}>
                        {item.label}
                      </Option>
                    ))}
                  </Select>
                ) : (
                  <Text variant="secondary">{selectedModel}</Text>
                )}
              </Grid>
            )}
            {selectedHost === OpenAIHost.Azure && (
              <>
                {(apiVersion || editFields) && (
                  <Grid
                    container={{
                      cols: 'repeat(2, 33% 65%)',
                      gap: 1,
                      alignItems: 'center',
                    }}
                  >
                    <CustomLabel
                      label={translate('api_version')}
                      showRequired={editFields ? true : false}
                      tooltipInfo={translate('api_version_tooltip')}
                    />
                    {editFields ? (
                      <Input
                        name="apiVersion"
                        label={translate('api_version')}
                        autoComplete="off"
                        type="text"
                        labelHidden={true}
                        info={errors.apiVersion?.message}
                        status={errors.apiVersion?.message ? 'error' : undefined}
                        ref={register({ required: translate('api_version_error') })}
                      />
                    ) : (
                      <Text variant="secondary">{apiVersion}</Text>
                    )}
                  </Grid>
                )}
                {(clientId || editFields) && (
                  <Grid
                    container={{
                      cols: 'repeat(2, 33% 65%)',
                      gap: 1,
                      alignItems: 'center',
                    }}
                  >
                    <CustomLabel
                      label={translate('client_id')}
                      showRequired={editFields ? true : false}
                      tooltipInfo={translate('client_id_tooltip')}
                    />
                    <Flex
                      container={{
                        direction: 'row',
                        alignItems: 'end',
                        itemGap: 1,
                      }}
                    >
                      {editFields || !maskData.maskClientId ? (
                        <Flex item={{ grow: 1 }}>
                          <TextArea
                            name="clientId"
                            label="clientId"
                            autoComplete="off"
                            labelHidden={true}
                            required={true}
                            readOnly={!editFields}
                            style={{ minHeight: 100 }}
                            info={errors.clientId?.message}
                            status={errors.clientId?.message ? 'error' : undefined}
                            ref={register({ required: translate('client_id_error') })}
                          />
                        </Flex>
                      ) : (
                        <Text variant="secondary">{getMaskedKey(clientId, 35)}</Text>
                      )}
                      {!editFields && (
                        <>
                          <Button
                            style={tokenMaskerStyle}
                            onClick={() =>
                              setMaskData((prevState) => ({ ...prevState, maskClientId: !maskData.maskClientId }))
                            }
                          >
                            <Icon name={maskData.maskClientId ? 'eye' : 'eye-off'} />
                          </Button>
                        </>
                      )}
                    </Flex>
                  </Grid>
                )}
              </>
            )}
            {(apiKey || editFields) && selectedHost !== OpenAIHost.Pega && (
              <Grid
                container={{
                  cols: 'repeat(2, 33% 65%)',
                  gap: 1,
                  alignItems: 'center',
                }}
              >
                <CustomLabel
                  label={translate(selectedHost === OpenAIHost.Azure ? 'client_secret' : 'api_key')}
                  showRequired={editFields ? true : false}
                  tooltipInfo={translate(
                    selectedHost === OpenAIHost.Azure ? 'client_secret_tooltip' : 'api_key_tooltip',
                  )}
                />
                <Flex
                  container={{
                    direction: 'row',
                    alignItems: 'end',
                    itemGap: 1,
                  }}
                >
                  {editFields || !maskData.maskApiKey ? (
                    <Flex item={{ grow: 1 }}>
                      <TextArea
                        name="apiKey"
                        label="apiKey"
                        autoComplete="off"
                        type="text"
                        labelHidden={true}
                        required={true}
                        readOnly={!editFields}
                        style={{ minHeight: 100 }}
                        info={errors.apiKey?.message}
                        status={errors.apiKey?.message ? 'error' : undefined}
                        ref={register({
                          required: translate(
                            selectedHost === OpenAIHost.Azure ? 'client_secret_error' : 'api_key_error',
                          ),
                        })}
                      />
                    </Flex>
                  ) : (
                    <Text variant="secondary">{getMaskedKey(apiKey, 35)}</Text>
                  )}
                  {!editFields && (
                    <>
                      <Button
                        style={tokenMaskerStyle}
                        onClick={() =>
                          setMaskData((prevState: any) => ({ ...prevState, maskApiKey: !maskData.maskApiKey }))
                        }
                      >
                        <Icon name={maskData.maskApiKey ? 'eye' : 'eye-off'} />
                      </Button>
                    </>
                  )}
                </Flex>
              </Grid>
            )}
            {(selectedModel || editFields) && selectedHost === OpenAIHost.Azure && (
              <Grid
                container={{
                  cols: 'repeat(2, 33% 65%)',
                  gap: 1,
                  alignItems: 'center',
                }}
              >
                <Label>
                  {translate('selected_model')} {editFields && <CustomRequired />}
                </Label>
                {editFields ? (
                  <Flex
                    container={{
                      direction: 'row',
                      alignItems: 'start',
                      itemGap: 1,
                    }}
                  >
                    <Flex container style={{ paddingTop: '20px' }}>
                      <Button variant="primary" onClick={handleSubmit(getModels)} disabled={!formState.isValid}>
                        {translate('get_models')}
                      </Button>
                    </Flex>
                    <Flex item={{ grow: 1 }}>
                      <Select name="selectedModel" label={translate('selected_model')} ref={register()}>
                        {updatedAzureModels.map((item: any) => (
                          <Option value={item.id ? item.id : item} key={item.id ? item.id : item}>
                            {item.label ? item.label : item}
                          </Option>
                        ))}
                      </Select>
                    </Flex>
                  </Flex>
                ) : (
                  <Text variant="secondary">{selectedModel}</Text>
                )}
              </Grid>
            )}
          </>
        )}
      </Flex>
      <Grid
        container={{
          cols: '2fr 2fr ',
          colGap: 2,
        }}
        style={{ paddingTop: '1rem' }}
      >
        <Flex>
          <Button onClick={handleOnSubmit} variant="primary" disabled={!editFields || !formState.isValid}>
            {translate('save')}
          </Button>
        </Flex>
        <Flex
          container={{
            direction: 'column',
            alignItems: 'end',
          }}
        >
          <Button onClick={editFields ? discardChanges : editConnectionData} disabled={simulationStatus === 'start'}>
            {translate(editFields ? 'cancel' : 'edit')}
          </Button>
        </Flex>
      </Grid>

      <Flex style={{ paddingTop: '1rem' }}>
        <Button onClick={deleteConfirmation}>{translate('delete_connection_button')}</Button>
      </Flex>
    </>
  );
};

const CustomRequired: React.FC = () => {
  return <Label style={{ color: '#d91c29' }}> *</Label>;
};

export default CustomerAISimulatorConnection;
